<template>
	<Modal
		:visible="visible"
		:title="$t('account.externalApiKeys.createModal.header')"
		:ok-text="$t('account.externalApiKeys.createModal.saveButtonLabel')"
		:cancel-text="$t('account.externalApiKeys.cancelButtonLabel')"
		:confirm-loading="isCreating"
		@ok="onCreate"
		@cancel="closeModal"
	>
		<Form layout="vertical">
			<FormItem
				:label="$t('account.externalApiKeys.createModal.nameLabel')"
				v-bind="validateInfos.name"
			>
				<Input ref="nameFieldRef" v-model:value="form.name" />
			</FormItem>
			<ExternalApiKeyPermissionsEditor
				v-model:permissions="form.permissions"
				:domain-error-visible="domainErrorVisible"
			/>
		</Form>
	</Modal>
</template>

<script>
import { nextTick, reactive, ref, watch } from 'vue';
import { Form, FormItem, Input, Modal, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import { showErrorMessage } from '@/components/common/errorMessage';
import ExternalApiKeyPermissionsEditor from '@/components/account/ExternalApiKeyPermissionsEditor.vue';
import { hasDomainPermission } from '@/components/account/externalApiKeyPermissions';


const useForm = Form.useForm;


export default {
	components: {
		Form,
		FormItem,
		Input,
		Modal,
		ExternalApiKeyPermissionsEditor,
	},
	props: {
		visible: {
			type: Boolean,
			required: true,
		},
	},
	emits: ['close', 'created'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const nameFieldRef = ref(null);
		const isCreating = ref(false);
		const domainErrorVisible = ref(false);
		const form = reactive({
			name: '',
			permissions: [],
		});
		const rulesRef = reactive({
			name: [{ required: true, message: t('account.externalApiKeys.createModal.nameRequired') }],
		});
		const { validate, validateInfos, resetFields } = useForm(form, rulesRef);

		watch(() => props.visible, isVisible => {
			if (!isVisible) {
				return;
			}
			nextTick(() => {
				if (nameFieldRef.value?.focus) {
					nameFieldRef.value.focus();
				}
			});
		});

		watch(() => form.permissions, () => {
			if (domainErrorVisible.value && hasDomainPermission(form.permissions)) {
				domainErrorVisible.value = false;
			}
		}, { deep: true });

		const closeModal = () => {
			resetFields();
			form.permissions = [];
			domainErrorVisible.value = false;
			isCreating.value = false;
			emit('close');
		};

		const onCreate = () => {
			validate().then(() => {
				if (!hasDomainPermission(form.permissions)) {
					domainErrorVisible.value = true;
					return;
				}
				domainErrorVisible.value = false;
				isCreating.value = true;
				Api.externalApiKeyCreate(form.name, form.permissions, response => {
					isCreating.value = false;
					if (response.status === 'ok') {
						message.success(t('account.externalApiKeys.createSuccess'));
						const rawKey = response.raw_key;
						resetFields();
						form.permissions = [];
						emit('created', rawKey);
						emit('close');
					} else {
						showErrorMessage(response.errors);
					}
				});
			}).catch(() => {});
		};

		return {
			nameFieldRef,
			isCreating,
			form,
			validateInfos,
			domainErrorVisible,
			closeModal,
			onCreate,
		};
	},
};
</script>
